import { uploadKinds, uploadMimeSchema, type PersonFileListItem } from '@vigeo/shared';

/**
 * How the viewer shows a file, chosen by its content type (ADR-009 §4):
 * - `pdf`: drawn by pdf.js onto a canvas. Never an iframe, `<embed>` or the browser's PDF
 *   plugin — Chrome and Edge refuse PDFs under the endpoint's `CSP: sandbox`, and that stays.
 * - `image`: an object URL in `<img>`, revoked when the viewer closes.
 * - `download`: everything else on the allowlist. HEIC is here too: only Safari decodes it, so
 *   an `<img>` would be a broken picture everywhere HR actually works.
 */
export type ViewerKind = 'pdf' | 'image' | 'download';

export function viewerKindFor(contentType: string): ViewerKind {
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType === 'image/jpeg' || contentType === 'image/png') return 'image';
  return 'download';
}

export interface FileGroups {
  readonly personnel: readonly PersonFileListItem[];
  /** I-9 and health. Present only when the API returned any — it filters for the viewer. */
  readonly restricted: readonly PersonFileListItem[];
}

const newestFirst = (a: PersonFileListItem, b: PersonFileListItem) => b.committedAt.localeCompare(a.committedAt);

/** Segregated files are kept apart, newest first in each group. */
export function groupFiles(files: readonly PersonFileListItem[]): FileGroups {
  return {
    personnel: files.filter((f) => !f.segregated).sort(newestFirst),
    restricted: files.filter((f) => f.segregated).sort(newestFirst),
  };
}

export function typeLabel(contentType: string): string {
  const parsed = uploadMimeSchema.safeParse(contentType);
  return parsed.success ? uploadKinds[parsed.data].label : 'File';
}

export function formatByteLength(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A download name when the server sent none: the display name plus the type's extension. */
export function fallbackDownloadName(item: Pick<PersonFileListItem, 'displayName' | 'contentType'>): string {
  const parsed = uploadMimeSchema.safeParse(item.contentType);
  return `${item.displayName}${parsed.success ? (uploadKinds[parsed.data].extensions[0] ?? '') : ''}`;
}

/**
 * The legacy SharePoint link a retained step instance may still carry (#61). Nothing writes it
 * since ADR-009; it is shown as a legacy link, never as the stored file.
 */
export const LEGACY_LINK_LABEL = 'legacy link';
